import { memo } from 'react';
import { useSelector } from 'react-redux';

import { ClipType } from '@air/api/types';
import { PlayerSubtitlesButton } from '@air/component-player';
import { isPdfExtension } from '@air/utils-clips';

import { ImageSettingsButton } from '~/components/AssetModalV2/Content/Footer/components/ImageSettingsButton';
import { ImageZoomSlider } from '~/components/AssetModalV2/Content/Footer/components/ImageZoomSlider';
import { PDFOutlineToggleButton } from '~/components/AssetModalV2/Content/Footer/components/PDFOutlineToggleButton';
import { PDFSettingsButton } from '~/components/AssetModalV2/Content/Footer/components/PDFSettingsButton';
import { PDFZoomSlider } from '~/components/AssetModalV2/Content/Footer/components/PDFZoomSlider';
import { VideoSettingsButton } from '~/components/AssetModalV2/Content/Footer/components/VideoSettingsButton';
import { useAssetModalContentControlsDensity } from '~/components/AssetModalV2/Content/Footer/contexts/AssetModalContentControlsDensityContext';
import { PublicDownloadButton } from '~/components/AssetModalV2/Content/Footer/PublicAssetComponents/PublicDownloadButton';
import { AssetModalContentFooter } from '~/components/v2/components/asset-modal/desktop/content/footer/AssetModalContentFooter';
import { AssetModalContentFooterColumn } from '~/components/v2/components/asset-modal/desktop/content/footer/AssetModalContentFooterColumn';
import { AssetModalContentFooterFullscreenButton } from '~/components/v2/components/asset-modal/desktop/content/footer/AssetModalContentFooterFullscreenButton';
import { AssetModalContentFooterThemeToggleButton } from '~/components/v2/components/asset-modal/desktop/content/footer/AssetModalContentFooterThemeToggleButton';
import { AssetModalContentFooterVideoTimeControls } from '~/components/v2/components/asset-modal/desktop/content/footer/AssetModalContentFooterVideoTimeControls';
import { AssetModalFooterVideoControls } from '~/components/v2/components/asset-modal/desktop/content/footer/AssetModalFooterVideoControls';
import { AssetModalContentHeaderPdfPageControls } from '~/components/v2/components/asset-modal/desktop/content/header/AssetModalContentHeaderPdfPageControls';
import { useAssetModalThemeToggle } from '~/components/v2/components/asset-modal/hooks/useAssetModalThemeToggle';
import {
  centralizedClipExtSelector,
  centralizedClipIsPDFTypeSelector,
  centralizedClipTypeSelector,
} from '~/store/centralizedClip/selectors';

export type PublicAssetModalContentFooterProps = {
  includeThemeControls?: boolean;
};

export const PublicAssetModalContentFooter = memo(
  ({ includeThemeControls = true }: PublicAssetModalContentFooterProps) => {
    const clipType = useSelector(centralizedClipTypeSelector);
    const isPdfRenderedFile = useSelector(centralizedClipIsPDFTypeSelector);
    const clipExt = useSelector(centralizedClipExtSelector);
    const { isCondensed } = useAssetModalContentControlsDensity();
    const { onSelectTheme, theme } = useAssetModalThemeToggle();

    const isVideo = clipType === ClipType.video;
    const isAudio = clipType === ClipType.audio;
    const isPhoto = !isPdfRenderedFile && (clipType === ClipType.photo || clipType === ClipType.animated);
    const isPdfFile = isPdfExtension(clipExt);
    const shouldCollapseVideoFooterControls = isVideo && isCondensed;
    const shouldShowThemeControls = includeThemeControls && !shouldCollapseVideoFooterControls;

    return (
      <AssetModalContentFooter>
        <AssetModalContentFooterColumn side="left">
          {isPhoto && <ImageZoomSlider />}
          {isPdfRenderedFile && (
            <>
              <PDFOutlineToggleButton />
              <PDFZoomSlider />
            </>
          )}
          {(isVideo || isAudio) && (
            <AssetModalFooterVideoControls hidePlaybackRate={shouldCollapseVideoFooterControls} />
          )}
        </AssetModalContentFooterColumn>

        <AssetModalContentFooterColumn side="center">
          {(isVideo || isAudio) && <AssetModalContentFooterVideoTimeControls />}
          {isPdfFile && <AssetModalContentHeaderPdfPageControls />}
        </AssetModalContentFooterColumn>

        <AssetModalContentFooterColumn side="right">
          {shouldShowThemeControls && <AssetModalContentFooterThemeToggleButton />}
          {isPhoto && <ImageSettingsButton />}
          {isPdfFile && <PDFSettingsButton />}
          {isVideo && (
            <VideoSettingsButton
              includePlaybackRateOptions={shouldCollapseVideoFooterControls}
              includeSubtitleOptions={shouldCollapseVideoFooterControls}
              includeThemeOption={includeThemeControls && shouldCollapseVideoFooterControls}
              onSelectTheme={onSelectTheme}
              theme={theme}
            />
          )}
          {isVideo && !shouldCollapseVideoFooterControls && <PlayerSubtitlesButton />}
          <AssetModalContentFooterFullscreenButton />
          <PublicDownloadButton isIconOnly={isCondensed} />
        </AssetModalContentFooterColumn>
      </AssetModalContentFooter>
    );
  },
);

PublicAssetModalContentFooter.displayName = 'PublicAssetModalContentFooter';
